import { useCallback, useState } from "react";

// Per-cell save feedback for the Bottom Up grids. A cell is "saved" (transient
// green ring, fades after SAVE_STATUS_FADE_MS) or "error" (red ring that
// persists until that cell is successfully re-saved). A single flash message
// accompanies errors. Grouping/rendering happens in the grids; this only tracks
// status keyed by (product_external_id, period_date).

export type CellStatus = "saved" | "error";

export interface CellRef {
  productExternalId: string;
  periodDate: string;
}

export interface SaveStatusState {
  cells: Record<string, CellStatus>;
  errorMessage: string | null;
}

export const EMPTY_SAVE_STATUS: SaveStatusState = { cells: {}, errorMessage: null };

export const SAVE_STATUS_FADE_MS = 1500;

export const SAVE_ERROR_MESSAGE =
  "Couldn't save your changes. Please check your connection and try again.";

export const SAVED_CELL_RING = "0 0 0 1.5px var(--success)";
export const ERROR_CELL_RING = "0 0 0 1.5px var(--destructive)";

export function cellKey(productExternalId: string, periodDate: string): string {
  return `${productExternalId}::${periodDate}`;
}

export function saveStatusRing(status: CellStatus | undefined): string | undefined {
  if (status === "saved") return SAVED_CELL_RING;
  if (status === "error") return ERROR_CELL_RING;
  return undefined;
}

// Success: mark every cell in the batch green and clear any active error flash.
export function applySaved(state: SaveStatusState, cells: CellRef[]): SaveStatusState {
  const next = { ...state.cells };
  for (const c of cells) next[cellKey(c.productExternalId, c.periodDate)] = "saved";
  return { cells: next, errorMessage: null };
}

// Failure: mark every cell in the batch red (persistent) and raise the flash.
export function applyError(
  state: SaveStatusState,
  cells: CellRef[],
  message: string,
): SaveStatusState {
  const next = { ...state.cells };
  for (const c of cells) next[cellKey(c.productExternalId, c.periodDate)] = "error";
  return { cells: next, errorMessage: message };
}

// Fade timer: drop only cells still marked "saved". A cell re-marked "error"
// after the batch saved keeps its red ring.
export function clearSaved(state: SaveStatusState, cells: CellRef[]): SaveStatusState {
  const next = { ...state.cells };
  let changed = false;
  for (const c of cells) {
    const k = cellKey(c.productExternalId, c.periodDate);
    if (next[k] === "saved") {
      delete next[k];
      changed = true;
    }
  }
  return changed ? { ...state, cells: next } : state;
}

export function clearError(state: SaveStatusState): SaveStatusState {
  return state.errorMessage === null ? state : { ...state, errorMessage: null };
}

export function useSaveStatus() {
  const [state, setState] = useState<SaveStatusState>(EMPTY_SAVE_STATUS);

  const markSaved = useCallback((cells: CellRef[]) => {
    if (cells.length === 0) return;
    setState((s) => applySaved(s, cells));
    setTimeout(() => setState((s) => clearSaved(s, cells)), SAVE_STATUS_FADE_MS);
  }, []);

  const markError = useCallback((cells: CellRef[], message: string = SAVE_ERROR_MESSAGE) => {
    if (cells.length === 0) return;
    setState((s) => applyError(s, cells, message));
  }, []);

  const dismissError = useCallback(() => setState((s) => clearError(s)), []);

  const statusFor = useCallback(
    (productExternalId: string, periodDate: string): CellStatus | undefined =>
      state.cells[cellKey(productExternalId, periodDate)],
    [state.cells],
  );

  return { statusFor, markSaved, markError, dismissError, errorMessage: state.errorMessage };
}
